/**
 * The decisions a `/r/CODE` page makes before it draws its first gate.
 *
 * Pure, so each one can be tested without a browser: which path is a room,
 * whether the room has a seat for this visitor, and whether a refusal off the
 * socket means the room is full.
 */

/** What `/r/…` names: a room code, something that is not one, or not a room path at all. */
export type RoomPath = { kind: 'room'; code: string } | { kind: 'malformed'; text: string } | null

/**
 * A room path, read.
 *
 * A well-formed code is four letters or numbers, as the server mints them. A
 * path that starts `/r/` but carries anything else used to fall through to the
 * landing page with the bad URL still in the bar and no word about it; it is
 * named here so the page can say what was wrong.
 */
export function parseRoomPath(pathname: string): RoomPath {
  const room = /^\/r\/([A-Za-z0-9]{4})\/?$/.exec(pathname)
  if (room) return { kind: 'room', code: room[1].toUpperCase() }
  const other = /^\/r\/([^/]*)\/?$/.exec(pathname)
  if (!other) return null
  let text = other[1]
  try {
    text = decodeURIComponent(text)
  } catch {
    // Leave a malformed escape as it was typed.
  }
  return { kind: 'malformed', text: text.slice(0, 40) }
}

/** The answer `GET /api/rooms/:code` gives for a room that exists. */
export interface RoomSummary {
  playerCount: number
  /** Rows in the room — connected, or held for somebody coming back. Absent from an older server. */
  seated?: number
}

/**
 * Whether this visitor can expect a seat.
 *
 * A room is full for a stranger when every seat has a row, including a row
 * held for somebody who dropped — that seat is theirs for the grace window, not
 * the next visitor's. It is *not* full for a page holding a claim on one of
 * those rows: that is the person the row is being held for, coming back.
 *
 * Asked before the gates, so a third visitor is told at once rather than after
 * a microphone check and a latency test for a game they cannot join.
 */
export function roomIsFullFor(summary: RoomSummary, holdsSeat: boolean): boolean {
  if (holdsSeat) return false
  if (typeof summary.seated !== 'number') return false
  return summary.seated >= summary.playerCount
}

/** The server's refusal for a full room (`Game ABCD is full.`), recognised. */
export function isRoomFullMessage(message: string): boolean {
  return /^Game [A-Z0-9]{4} is full\.$/.test(message)
}
